/** Кусочки UI поставок (таблица и карточка): путь чипами, бейдж метода, сверка, места сейчас,
 *  флаги. Портал ничего не пересчитывает — только поля бэкенда; null — «нет данных». */
import React from "react";

import type { WbSupply } from "@/api/wbRegions";
import { cn } from "@/lib/utils";
import { int, intSigned, pct, pctSigned } from "../ozon-analytics/format";
import { Missing, Qty } from "./cells";
import {
  flagLabel,
  methodLabel,
  placeName,
  PLANNED_LABEL,
  profileMarginTitle,
  supplyPath,
  type ChipTone,
} from "./supplyFormat";

const TONE: Record<ChipTone, string> = {
  plain: "border-border bg-background",
  hint: "border-dashed border-muted-foreground/40 bg-muted/30 text-muted-foreground",
  danger: "border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300",
};

/** Путь поставки: Приёмка → attr19 (подсказка) → Оценка. `large` — для карточки. */
export function PathChips({ s, large = false }: { s: WbSupply; large?: boolean }): React.ReactElement {
  const chips = supplyPath(s);
  return (
    <div className={cn("flex flex-wrap items-center gap-1", large ? "text-sm" : "text-[11px]")} data-testid="supply-path">
      {chips.map((c, i) => (
        <React.Fragment key={c.key}>
          {i > 0 && (
            <span className="text-muted-foreground" aria-hidden>
              →
            </span>
          )}
          <span
            className={cn("rounded border px-1.5 py-0.5 leading-tight", TONE[c.tone], large && "px-2 py-1")}
            title={c.title || undefined}
            data-testid={`path-${c.key}`}
            data-tone={c.tone}
          >
            <span className={cn("block text-muted-foreground", large ? "text-[11px]" : "text-[10px]")}>{c.label}</span>
            {c.text ?? <Missing />}
            {c.note &&
              (c.tone === "danger" ? (
                <span className="block font-medium text-red-700 dark:text-red-400" data-testid="attr19-mismatch">
                  {c.note}
                </span>
              ) : (
                <span className="block text-[10px] text-muted-foreground" data-testid={`path-${c.key}-note`}>
                  {c.note}
                </span>
              ))}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

/** Бейдж метода оценки склада + уверенность; расшифровка и `confidence_reason` — при наведении.
 *  `profileMargin` (карточка поставки) — отрыв профиля покупателей подсказкой к методу. */
export function MethodBadge({
  s,
  profileMargin,
}: {
  s: Pick<WbSupply, "method" | "confidence" | "confidence_reason">;
  profileMargin?: number | null;
}): React.ReactElement {
  const m = methodLabel(s.method);
  const title = [
    m.long,
    s.confidence_reason && `уверенность: ${s.confidence_reason}`,
    profileMargin !== undefined && profileMarginTitle(profileMargin),
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid="method">
      <span
        className={cn(
          "cursor-help rounded px-1.5 py-0.5 text-[11px]",
          m.known ? "bg-muted" : "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
        )}
        title={title}
        data-method={s.method ?? ""}
      >
        {m.short}
      </span>
      <span className="tabular-nums text-muted-foreground" title="уверенность оценки склада поставки">
        {s.confidence === null ? <Missing text="—" /> : pct(s.confidence)}
      </span>
    </span>
  );
}

/** Сверка поставки: flag — красным (при «не значимо» — серым с причиной); not_computed —
 *  «не посчитана: …»; incomplete — с пометкой «сверка неполная». */
export function ReconCell({ s }: { s: WbSupply }): React.ReactElement {
  if (s.recon_status === "not_computed") {
    return (
      <span className="text-muted-foreground" data-testid="recon" data-recon="not_computed">
        не посчитана: {s.recon_not_computed_reason ?? "причина не указана"}
      </span>
    );
  }
  const value = (
    <>
      {s.recon_diff === null ? <Missing /> : intSigned(s.recon_diff)} шт. ·{" "}
      {s.recon_diff_pct === null ? <Missing text="—" /> : pctSigned(s.recon_diff_pct / 100)}
    </>
  );
  if (s.recon_status === "flag") {
    const muted = s.recon_flag_muted;
    return (
      <span
        data-testid="recon"
        data-recon="flag"
        data-muted={muted ? "1" : "0"}
        className={cn("tabular-nums", muted ? "text-muted-foreground" : "font-medium text-red-700 dark:text-red-400")}
        title={muted ? `флаг сверки не значим: ${s.recon_muted_reason ?? ""}` : "расхождение сверки > 10 %"}
      >
        {value}
        {muted && <span className="block text-[10px]">не значимо: {s.recon_muted_reason}</span>}
      </span>
    );
  }
  return (
    <span className="tabular-nums" data-testid="recon" data-recon={s.recon_status}>
      {value}
      {s.recon_status === "incomplete" && (
        <span
          className="block text-[10px] text-amber-700 dark:text-amber-400"
          title="recon_complete = false: поставка до 01.04.2026 — сверка неполная, флаг false не значит «норма»"
          data-testid="recon-incomplete"
        >
          сверка неполная
        </span>
      )}
    </span>
  );
}

/** Места сейчас: «склад, регион — шт.», заморожено — пометкой; мест нет — «штук сейчас нет». */
export function PlacesList({ s }: { s: WbSupply }): React.ReactElement {
  if (s.places.length === 0) return <span className="text-muted-foreground">штук сейчас нет</span>;
  return (
    <ul className="space-y-0.5" data-testid="places">
      {s.places.map((p, i) => (
        <li key={i} className="whitespace-nowrap" title={`уверенность ${p.confidence === null ? "—" : pct(p.confidence)}; способы: ${p.methods ?? "—"}`}>
          {placeName(p)} — <Qty v={p.qty} />
          {p.frozen && <span className="ml-1 text-muted-foreground">(заморожено)</span>}
        </li>
      ))}
    </ul>
  );
}

/** Пометка «запланирована» (ещё не принята, контракт v1.3). */
export function PlannedMark({ s }: { s: Pick<WbSupply, "planned"> }): React.ReactElement | null {
  if (!s.planned) return null;
  return (
    <span
      className="rounded bg-sky-100 px-1 text-[10px] text-sky-900 dark:bg-sky-950 dark:text-sky-300"
      title="ещё не принята: статус WB «Запланировано» / «Отгрузка разрешена»"
      data-testid="planned"
    >
      {PLANNED_LABEL}
    </span>
  );
}

export function FlagsList({ s }: { s: WbSupply }): React.ReactElement {
  if (s.flags.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex flex-wrap gap-1" data-testid="flags">
      {s.flags.map((f) => {
        const muted = f === "recon_flag" && s.recon_flag_muted;
        return (
          <span
            key={f}
            data-flag={f}
            className={cn(
              "rounded px-1 text-[10px]",
              muted ? "bg-muted text-muted-foreground" : "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
            )}
            title={muted ? `не значимо: ${s.recon_muted_reason ?? ""}` : undefined}
          >
            {flagLabel(f)}
          </span>
        );
      })}
    </span>
  );
}

/** Принято · продано · возвраты · вывоз (источник принятого — в подсказке). */
export function FlowCell({ s }: { s: WbSupply }): React.ReactElement {
  const q = (v: number | null): string => (v === null ? "нет данных" : int(v));
  return (
    <span
      className="tabular-nums"
      data-testid="flow"
      title={`заявлено ${q(s.qty_planned)}; принято — ${s.qty_accepted_source ?? "источник не указан"}; в продаже ${q(s.qty_ready_for_sale)}`}
    >
      <Qty v={s.qty_accepted} /> · <Qty v={s.sold_fbo} /> · <Qty v={s.returns} /> · <Qty v={s.to_seller} />
    </span>
  );
}
